import { Box } from "@material-ui/core"
import { ReactComponent as AddIcon } from "CryptioUI/assets/icons/add.svg"
import React, { useCallback, useMemo } from "react"

import api from "services/api"
import { GetLabelDto, MovementCounterPartyDto, NewFullTransactionDto, NewInListMovementDto } from "services/api/openapi"
import { Mixpanel } from "services/mixpanel"
import { pluralize } from "services/utils/textUtils"
import { UseQueryResult } from "react-query"
import PermissionDisabled from "components/Permission/PermissionDisabled"
import { UncontrolledLoadingButton } from "components/LoadingButton"
import { iconStyleWhite } from "CryptioUI/Utilities/config"
import { useToast } from "CryptioUI/Toaster"
import { toastCatch } from "components/ReactHookForm/utils"

interface AddLabelButtonProps {
  nonTaxableLabels: GetLabelDto[]
  nonTaxablelabelsToAdd: GetLabelDto[]
  selectedGuessedOtherParty: MovementCounterPartyDto | null
  confirmedInternalTransfer: boolean
  transaction: NewFullTransactionDto
  onClose: () => void
  counterParties: UseQueryResult<MovementCounterPartyDto[], unknown>
  labelsToAdd: GetLabelDto[]
  setLabelsToAdd: (newLabel: GetLabelDto[]) => void
  isInternalTransfer: boolean
  mainMvt: NewInListMovementDto | undefined
}

const AddLabelButton = (props: AddLabelButtonProps) => {
  const toast = useToast()

  const {
    nonTaxablelabelsToAdd,
    selectedGuessedOtherParty,
    confirmedInternalTransfer,
    transaction,
    onClose,
    counterParties,
    labelsToAdd,
    setLabelsToAdd,
    isInternalTransfer,
    mainMvt,
  } = props

  const { mutateAsync: bulkUpdateMovementsMutation } = api.transaction.useBulkUpdateMovements()
  const { mutateAsync: insertInternalTransfersMutation } = api.transaction.useInsertInternalTransfers()

  const numberNonTaxablelabelsToAdd = useCallback((): number => {
    const isIncompleteInternalTransfer = isInternalTransfer && !selectedGuessedOtherParty ? -1 : 0

    return nonTaxablelabelsToAdd.length + isIncompleteInternalTransfer
  }, [isInternalTransfer, nonTaxablelabelsToAdd.length, selectedGuessedOtherParty])

  const calculeNbNewLabel = useMemo(() => {
    return labelsToAdd.length + numberNonTaxablelabelsToAdd()
  }, [labelsToAdd, numberNonTaxablelabelsToAdd])

  const buttonDesable = (): boolean => {
    return (
      (labelsToAdd.length || numberNonTaxablelabelsToAdd() ? false : true) &&
      !(confirmedInternalTransfer && isInternalTransfer && selectedGuessedOtherParty)
    )
  }

  const tooltopError = (): string => {
    if (isInternalTransfer) {
      if (!counterParties?.data?.length) {
        return `Label internal transfer can't be add without a valid counter party`
      }
    }
    if (buttonDesable()) {
      return "No label selected"
    }
    return ""
  }

  const addSelectedLabels = async () => {
    if (labelsToAdd.length > 0 || numberNonTaxablelabelsToAdd() > 0 || isInternalTransfer) {
      try {
        if (isInternalTransfer && selectedGuessedOtherParty) {
          await insertInternalTransfersMutation({
            createInternalMovementLinkDto: {
              movementId: mainMvt?.id ?? "",
              counterMovementId: selectedGuessedOtherParty?.id ?? null,
            },
          })
        }

        const labelIdToAdd = labelsToAdd.map((l) => l.id)
        const index = nonTaxablelabelsToAdd.findIndex((label) => label.name === "Non taxable event")

        if (index !== -1) {
          labelIdToAdd.push(nonTaxablelabelsToAdd[index].id)
        }

        if (labelIdToAdd.length > 0) {
          await bulkUpdateMovementsMutation({
            movementBulkUpdateDto: {
              labelIdsToAdd: labelIdToAdd,
              individualTransactions: { transactionIds: [transaction.id] },
            },
          })
        }

        toast.open("Label list updated", { variant: "success" })
      } catch (e) {
        toastCatch(e, toast)
      }
      labelsToAdd.forEach((x) => Mixpanel.track("TransactionLabelAssigned", { labelName: x.name }))
    }
    setLabelsToAdd([])
    onClose()
  }

  return (
    <Box mt={1}>
      <PermissionDisabled
        permission="can_modify_transaction"
        action="modify a transaction"
        titlePermissionEnable={tooltopError()}
      >
        <Box>
          <Box mt={3}>
            <UncontrolledLoadingButton
              disabled={buttonDesable()}
              fullWidth
              Icon={<AddIcon className={iconStyleWhite} />}
              onClick={addSelectedLabels}
            >
              Add
              {pluralize(labelsToAdd.length + numberNonTaxablelabelsToAdd() > 1, ` ${calculeNbNewLabel} label`)}
            </UncontrolledLoadingButton>
          </Box>
        </Box>
      </PermissionDisabled>
    </Box>
  )
}

export default AddLabelButton
